import { useState, useEffect, useCallback, useRef } from 'react';
import {
  eyebrowStyle, h1Style, h2Style, subtitleStyle,
  cardStyle, chipStyle, secondaryBtnStyle, backBtnStyle, ulStyle, topicLabel,
} from './tokens';
import EmptyState from './EmptyState';
import ShareExperienceSheet from './ShareExperienceSheet';
import ReportModal from './ReportModal';
import {
  fetchQuestion, fetchExperiences, fetchRelatedQuestions,
  isSaved, toggleSave, signedAttachmentUrl, fetchOrGenerateUnderstanding,
} from '../../lib/communityService';

/**
 * QuestionDetailScreen — full detail: question, understanding (Theo-generated
 * and cached in DB), shared experiences, related, save/share/report.
 * Accepts a `question` prop that may be a stub (from feed) OR just an id.
 */
export default function QuestionDetailScreen({ userId, question, onBack, onOpenQuestion, onNavigate }) {
  const [q, setQ]           = useState(question || null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [experiences, setExperiences]     = useState([]);
  const [related, setRelated]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [loadError, setLoadError]         = useState(null);
  const [saved, setSaved]                 = useState(false);
  const [savingBusy, setSavingBusy]       = useState(false);
  const [shareOpen, setShareOpen]         = useState(false);
  const [reportOpen, setReportOpen]       = useState(false);
  const [copyToast, setCopyToast]         = useState(false);
  const [copyError, setCopyError]         = useState(false);
  const [understanding, setUnderstanding] = useState(null);
  const [understandingLoading, setUnderstandingLoading] = useState(true);
  // Prevent StrictMode / rerun re-invocation of the LLM call for the same question.
  const understandingInFlightRef = useRef(null);
  // Keep the latest stub prop reachable inside `load` without adding the
  // whole object to the useCallback dep array (which would re-fire whenever
  // the parent recreates the object).
  const stubQuestionRef = useRef(question);
  useEffect(() => { stubQuestionRef.current = question; }, [question]);
  const questionId = question?.id;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setUnderstanding(null);
    setUnderstandingLoading(true);
    let full = null;
    try {
      // ALWAYS fetch the full row on open so we get cached `understanding`,
      // `related_refs`, and `tags` — feed stubs deliberately don't select
      // those large columns. If the fetch fails (e.g. offline), fall back
      // to the stub so the page still renders the header + experiences.
      full = await fetchQuestion(questionId);
      if (!full && stubQuestionRef.current?.description) full = stubQuestionRef.current;
    } catch (e) {
      console.error('[community] fetchQuestion failed', e);
      if (stubQuestionRef.current?.description) full = stubQuestionRef.current;
      else setLoadError(e);
    }
    setQ(full);
    if (!full) { setLoading(false); setUnderstandingLoading(false); return; }

    const [exp, rel, savedNow] = await Promise.all([
      fetchExperiences(full.id).catch(() => []),
      fetchRelatedQuestions({ questionId: full.id, topic: full.topic, title: full.title, related_refs: full.related_refs, limit: 3 }).catch(() => []),
      userId ? isSaved(userId, full.id).catch(() => false) : Promise.resolve(false),
    ]);
    setExperiences(exp);
    setRelated(rel);
    setSaved(savedNow);
    if (full.attachment_url) {
      const url = await signedAttachmentUrl(full.attachment_url).catch(() => null);
      setAttachmentUrl(url);
    } else {
      setAttachmentUrl(null);
    }
    setLoading(false);

    // Understanding runs after the main content is on-screen. Guard against
    // double-invocation (StrictMode dev double-mount, rerender) by keeping
    // an in-flight promise keyed by question id.
    if (understandingInFlightRef.current?.id === full.id) {
      try {
        const u = await understandingInFlightRef.current.promise;
        setUnderstanding(u);
      } finally {
        setUnderstandingLoading(false);
      }
      return;
    }
    const promise = fetchOrGenerateUnderstanding(full).catch((e) => {
      console.error('[community] understanding', e);
      return null;
    });
    understandingInFlightRef.current = { id: full.id, promise };
    const u = await promise;
    setUnderstanding(u);
    setUnderstandingLoading(false);
  }, [questionId, userId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!userId || savingBusy) return;
    setSavingBusy(true);
    const next = await toggleSave(userId, q.id, saved);
    setSaved(next);
    setSavingBusy(false);
  };

  const onShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}#/community/${q.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: q.title, text: q.title, url });
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setCopyToast(true);
        setTimeout(() => setCopyToast(false), 2200);
      } else {
        // Extremely old / restricted browsers: nothing we can do silently.
        setCopyError(true);
        setTimeout(() => setCopyError(false), 2600);
      }
    } catch (e) {
      // User cancelled navigator.share → no-op. Clipboard permission denied → surface it.
      if (e && e.name !== 'AbortError') {
        setCopyError(true);
        setTimeout(() => setCopyError(false), 2600);
      }
    }
  };

  if (loadError && !q) {
    return (
      <div style={{ padding: '20px 16px 120px' }} data-testid="question-detail-error">
        <button onClick={onBack} style={backBtnStyle} data-testid="detail-back-btn">← Back</button>
        <p style={{ ...subtitleStyle, textAlign: 'center', marginTop: 40 }}>
          We couldn&rsquo;t open this question right now.
        </p>
        <button
          onClick={load}
          data-testid="detail-retry"
          style={{ display: 'block', margin: '16px auto 0', padding: '10px 18px', borderRadius: 999, border: '1px solid rgba(109,94,245,.30)', background: 'rgba(109,94,245,.08)', color: '#6D5EF5', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (loading || !q) {
    return (
      <div style={{ padding: '20px 16px 120px' }} data-testid="question-detail-loading">
        <button onClick={onBack} style={backBtnStyle} data-testid="detail-back-btn">← Back</button>
        <p style={{ ...subtitleStyle, textAlign: 'center', marginTop: 40 }}>Loading question…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 16px 120px' }} data-testid="question-detail-screen">
      <button onClick={onBack} style={backBtnStyle} data-testid="detail-back-btn">← Back</button>

      {/* Question */}
      <section style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span style={chipStyle}>{topicLabel(q.topic)}</span>
          <span style={{ fontSize: 11.5, color: '#8A847C' }}>{q.author || 'Anonymous Member'} · {q.when}</span>
        </div>
        <h1 style={{ ...h1Style, fontSize: 26 }}>{q.title}</h1>
        {q.description && q.description.trim() && q.description.trim() !== q.title.trim() && (
          <p style={{ ...subtitleStyle, marginTop: 10, color: '#44454C', whiteSpace: 'pre-wrap' }}>
            {q.description}
          </p>
        )}

        {attachmentUrl ? (
          <a
            href={attachmentUrl} target="_blank" rel="noopener noreferrer"
            data-testid="detail-image-attachment"
            style={{ display: 'block', marginTop: 14, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(0,0,0,.06)' }}
          >
            <img src={attachmentUrl} alt="Attachment" style={{ width: '100%', display: 'block' }} />
          </a>
        ) : q.attachment_url ? (
          <div data-testid="detail-image-placeholder" style={{
            marginTop: 14, padding: 40, borderRadius: 16, textAlign: 'center',
            background: 'rgba(0,0,0,.02)', color: '#8A847C', fontSize: 12,
          }}>
            Attachment unavailable
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={onSave} disabled={savingBusy} style={{ ...secondaryBtnStyle, background: saved ? 'rgba(109,94,245,.08)' : '#fff', color: saved ? '#6D5EF5' : '#232323', borderColor: saved ? 'rgba(109,94,245,.30)' : 'rgba(0,0,0,.08)' }} data-testid="detail-save-btn">
            {saved ? 'Saved' : 'Save'}
          </button>
          <button onClick={onShare} style={secondaryBtnStyle} data-testid="detail-share-btn">Share</button>
          <button onClick={() => setReportOpen(true)} style={{ ...secondaryBtnStyle, color: '#C67A69', borderColor: 'rgba(198,122,105,.30)' }} data-testid="detail-report-btn">Report</button>
        </div>
        {copyToast && (
          <div data-testid="detail-share-copied" style={{ marginTop: 10, fontSize: 12, color: '#4F6D5D', textAlign: 'center' }}>
            Link copied to clipboard.
          </div>
        )}
        {copyError && (
          <div data-testid="detail-share-error" style={{ marginTop: 10, fontSize: 12, color: '#C67A69', textAlign: 'center' }}>
            Couldn&rsquo;t copy the link. Please try again.
          </div>
        )}
      </section>

      {/* Understanding — placeholder until Sprint 3 (Theo AI) */}
      <section style={{ marginTop: 28 }} data-testid="detail-understanding">
        <div style={{ ...eyebrowStyle, color: '#5E4F7F' }}>UNDERSTANDING</div>
        <div style={{
          padding: 26,
          borderRadius: 24,
          background: 'linear-gradient(160deg, #FFFFFF, #F7F3FF 92%)',
          border: '1px solid rgba(94,79,127,.14)',
          boxShadow: '0 24px 60px -28px rgba(94,79,127,.35), 0 6px 18px -10px rgba(94,79,127,.12)',
        }}>
          <h2 style={{ ...h2Style, fontSize: 22, marginTop: 0, marginBottom: 10 }}>
            What this may mean
          </h2>
          {understandingLoading ? (
            <p data-testid="understanding-loading" style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: '#6F6A66', fontStyle: 'italic' }}>
              Theo is thinking through this…
            </p>
          ) : understanding ? (
            <>
              <div data-testid="understanding-summary" style={{ fontSize: 15, lineHeight: 1.6, color: '#232323' }}>
                {String(understanding.summary || 'Theo could not compose a summary for this one just yet.')
                  .split(/\n\s*\n/)
                  .map((para, i, arr) => (
                    <p key={i} style={{ margin: 0, marginBottom: i < arr.length - 1 ? 12 : 0 }}>{para}</p>
                  ))}
              </div>

              {understanding.explanations?.length > 0 && (
                <>
                  <h3 style={{ ...h2Style, fontSize: 15, marginTop: 22, marginBottom: 8 }}>Possible explanations</h3>
                  <ul style={ulStyle} data-testid="understanding-explanations">
                    {understanding.explanations.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </>
              )}

              {understanding.when_to_speak?.length > 0 && (
                <>
                  <h3 style={{ ...h2Style, fontSize: 15, marginTop: 20, marginBottom: 8 }}>When to speak with a healthcare professional</h3>
                  <ul style={ulStyle} data-testid="understanding-when-to-speak">
                    {understanding.when_to_speak.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </>
              )}

              {understanding.references?.length > 0 && (
                <>
                  <h3 style={{ ...h2Style, fontSize: 15, marginTop: 20, marginBottom: 8 }}>References</h3>
                  <ul style={ulStyle} data-testid="understanding-references">
                    {understanding.references.map((line, i) => (
                      <li key={i} style={{ fontStyle: 'italic', color: '#6F6A66' }}>{line}</li>
                    ))}
                  </ul>
                </>
              )}

              {understanding.small_step && (
                <>
                  <h3 style={{ ...h2Style, fontSize: 15, marginTop: 20, marginBottom: 8 }}>Small step for today</h3>
                  <p data-testid="understanding-small-step" style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#232323' }}>
                    {understanding.small_step}
                  </p>
                </>
              )}

              {understanding.key_takeaway && (
                <div data-testid="understanding-key-takeaway" style={{
                  marginTop: 22, padding: '14px 16px', borderRadius: 14,
                  background: 'rgba(94,79,127,.06)',
                  border: '1px solid rgba(94,79,127,.16)',
                }}>
                  <div style={{ ...eyebrowStyle, color: '#5E4F7F', marginBottom: 6 }}>Key takeaway</div>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: '#232323' }}>
                    {understanding.key_takeaway}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p data-testid="understanding-error" style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: '#6F6A66' }}>
              Theo couldn&rsquo;t compose an evidence summary just now. Please try again in a moment.
            </p>
          )}
        </div>
      </section>

      {/* Shared Experiences */}
      <section style={{ marginTop: 28 }} data-testid="detail-experiences">
        <div style={eyebrowStyle}>SHARED EXPERIENCES</div>
        {experiences.length === 0 ? (
          <EmptyState
            testid="empty-experiences"
            title="No one has shared yet."
            copy="Be the first to share what helped you. Someone else is looking for exactly this."
            ctaLabel="Share your experience"
            onCta={() => setShareOpen(true)}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {experiences.map((s) => (
              <div key={s.id} style={{ ...cardStyle, padding: 16 }} data-testid={`experience-${s.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6F6A66' }}>{s.author} · {s.when}</div>
                  {s.edited && <span style={{ fontSize: 10, color: '#8A847C', fontStyle: 'italic' }}>edited</span>}
                </div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: '#232323', whiteSpace: 'pre-wrap' }}>{s.experience}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Next Steps */}
      <section style={{ marginTop: 28 }} data-testid="detail-next-steps">
        <div style={eyebrowStyle}>Continue Your Journey</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {[
            { key: 'wellness', label: 'Explore Wellness', tab: 'care' },
            { key: 'theo',     label: 'Talk to Theo',     tab: 'theo' },
            { key: 'symptoms', label: 'Track Symptoms',   tab: 'symptoms' },
            { key: 'hormones', label: 'View Hormones',    tab: 'hormones' },
          ].map((n) => (
            <button
              key={n.key}
              onClick={() => onNavigate?.(n.tab)}
              data-testid={`next-step-${n.key}`}
              style={{
                ...cardStyle, padding: 14, fontSize: 13, fontWeight: 600, color: '#232323',
                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                border: '1px solid rgba(0,0,0,.04)',
              }}
            >
              {n.label}
            </button>
          ))}
        </div>
      </section>

      {/* Related */}
      <section style={{ marginTop: 28 }} data-testid="detail-related">
        <div style={eyebrowStyle}>RELATED QUESTIONS</div>
        {related.length === 0 ? (
          <EmptyState
            testid="empty-related"
            title="Nothing related just yet."
            copy="As more questions come in, we&rsquo;ll show related ones here."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {related.map((r) => (
              <button
                key={r.id}
                onClick={() => onOpenQuestion?.(r)}
                data-testid={`related-${r.id}`}
                style={{ ...cardStyle, padding: 14, fontSize: 13.5, color: '#232323', textAlign: 'left', cursor: 'pointer', border: '1px solid rgba(0,0,0,.04)' }}
              >
                {r.title}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Sticky Share CTA */}
      <button
        onClick={() => setShareOpen(true)}
        data-testid="share-experience-open"
        style={{
          position: 'fixed', bottom: 88, left: 16, right: 16,
          padding: '14px 20px', borderRadius: 999,
          background: 'linear-gradient(90deg,#6D5EF5,#8A7FF7)',
          color: '#fff', fontSize: 14, fontWeight: 600, border: 'none',
          boxShadow: '0 12px 30px -10px rgba(109,94,245,.55)', cursor: 'pointer',
        }}
      >
        Share your experience
      </button>

      {shareOpen && (
        <ShareExperienceSheet
          userId={userId}
          questionId={q.id}
          onClose={() => setShareOpen(false)}
          onSent={(row) => setExperiences(prev => [row, ...prev])}
        />
      )}
      {reportOpen && (
        <ReportModal
          userId={userId}
          questionId={q.id}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
